import type { LocationAppointmentEligibilityPublicationV1 } from "@/app/booking/adapters/locationAppointmentCatalog";
import { createSpecialistServicesAppointmentDraft } from "@/app/booking/adapters/specialistServicesAppointmentDraft";
import {
  validateAppointmentBookingDraft,
  type AppointmentBookingDraftV1,
  type AppointmentServiceLineDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import { buildAppointmentSchedulePlan } from "@/app/booking/scheduling/appointmentSchedulePlan";
import { createAppointmentSchedulePlanStaffIndependentLineage } from "@/app/booking/scheduling/appointmentSchedulePlan";
import { parseAppointmentLocationEligibilityPublication } from "@/app/booking/scheduling/appointmentLocationEligibilityPublication";
import {
  resolveSpecialistServicesRouteData,
  type SpecialistServicesRouteData,
} from "@/app/locations/[id]/specialists/[userLocationId]/specialistRouteData";
import type { SpecialistServiceSelection } from "@/app/specialists/model";

export const SPECIALIST_SERVICES_ROUTE_CAPABILITY_SCHEMA_VERSION = 1 as const;

function createSpecialistRouteTaskGraphLineage(
  draft: AppointmentBookingDraftV1,
) {
  const result = buildAppointmentSchedulePlan(draft);

  if (!result.ok) {
    return null;
  }

  const { origin: omittedOrigin, ...lineage } =
    createAppointmentSchedulePlanStaffIndependentLineage(result.plan);
  void omittedOrigin;
  return lineage;
}

export interface SpecialistServicesRouteCapabilityV1 {
  schemaVersion: typeof SPECIALIST_SERVICES_ROUTE_CAPABILITY_SCHEMA_VERSION;
  kind: "specialist-services-route-capability";
  appointmentDraftSchemaVersion: AppointmentBookingDraftV1["schemaVersion"];
  draftId: string;
  locationId: string;
  userLocationId: string;
  catalogVersion: string;
  eligibilityProviderId: string;
  eligibilityProviderRevision: string;
  taskBindings: readonly {
    taskId: string;
    serviceId: string;
    offeringId: string;
  }[];
  selections: readonly SpecialistServiceSelection[];
  routeServiceIds: readonly string[];
  routeOfferingIds: readonly string[];
  routeStaffIndependentPlan: NonNullable<
    ReturnType<typeof createSpecialistRouteTaskGraphLineage>
  >;
}

export interface SpecialistServicesRouteCapabilitySource {
  resolveRouteData: (
    locationId: string,
    userLocationId: string,
  ) => SpecialistServicesRouteData | null;
}

export const specialistServicesRouteCapabilityFixtureSource: SpecialistServicesRouteCapabilitySource =
  {
    resolveRouteData: resolveSpecialistServicesRouteData,
  };

function cloneSelection(
  selection: SpecialistServiceSelection,
): SpecialistServiceSelection {
  return {
    serviceId: selection.serviceId,
    variantId: selection.variantId,
    ...(selection.addOns
      ? { addOns: selection.addOns.map((addOn) => ({ ...addOn })) }
      : {}),
    ...(selection.bundleItems
      ? {
          bundleItems: selection.bundleItems.map((item) => ({
            ...item,
            addOns: item.addOns.map((addOn) => ({ ...addOn })),
          })),
        }
      : {}),
  };
}

function getSelectionFromDraftLine(
  line: AppointmentServiceLineDraft,
): SpecialistServiceSelection {
  return line.selection.kind === "service"
    ? {
        serviceId: line.selection.serviceId,
        variantId: line.selection.variantId,
        addOns: line.selection.addOns.map((addOn) => ({ ...addOn })),
      }
    : {
        serviceId: line.selection.serviceId,
        variantId: null,
        bundleItems: line.selection.bundleItems.map((item) => ({
          bundleItemId: item.bundleItemId,
          serviceId: item.serviceId,
          variantId: item.variantId,
          addOns: item.addOns.map((addOn) => ({ ...addOn })),
        })),
      };
}

function getOrganizerSelections(draft: AppointmentBookingDraftV1) {
  const organizer = draft.participants.find(
    ({ participantId }) => participantId === draft.organizerParticipantId,
  );

  return organizer
    ? [...organizer.serviceLines]
        .sort((left, right) => left.selectionOrder - right.selectionOrder)
        .map(getSelectionFromDraftLine)
    : null;
}

function haveExactSerializableStructure(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function resolveCommonSpecificProfessional(draft: AppointmentBookingDraftV1) {
  const planResult = buildAppointmentSchedulePlan(draft);

  if (!planResult.ok || planResult.plan.tasks.length === 0) {
    return null;
  }

  const userLocationIds = new Set(
    planResult.plan.tasks.flatMap(({ staffPreference }) =>
      staffPreference.kind === "specific"
        ? [staffPreference.userLocationId]
        : [],
    ),
  );

  if (
    userLocationIds.size !== 1 ||
    planResult.plan.tasks.some(
      ({ staffPreference }) => staffPreference.kind !== "specific",
    )
  ) {
    return null;
  }

  return {
    userLocationId: [...userLocationIds][0]!,
    taskBindings: planResult.plan.tasks.map((task) => ({
      taskId: task.taskId,
      serviceId: task.serviceId,
      offeringId: task.offeringId,
    })),
  };
}

/**
 * Resolves current, destination-owned authority for a standalone Specialist
 * Services document. Direct scheduling eligibility proves only that the
 * professional can execute each current task; the complete published route
 * catalog independently proves that Back can navigate to that document.
 *
 * The optional Location `Display professional profiles` capability is
 * intentionally absent. Turning off read-only profile actions must not hide a
 * still-published Specialist Services route, while a directly eligible team
 * member without that route always resolves null.
 */
export function resolveCurrentSpecialistServicesRouteCapability(input: {
  draft: AppointmentBookingDraftV1;
  currentLocationEligibilityPublication: LocationAppointmentEligibilityPublicationV1;
  source?: SpecialistServicesRouteCapabilitySource;
}): SpecialistServicesRouteCapabilityV1 | null {
  const validation = validateAppointmentBookingDraft(input.draft);
  const publication = parseAppointmentLocationEligibilityPublication(
    input.currentLocationEligibilityPublication,
  );

  if (!validation.valid || !publication) {
    return null;
  }

  const draft = validation.draft;
  const assignment = resolveCommonSpecificProfessional(draft);
  const selections = getOrganizerSelections(draft);

  if (
    !assignment ||
    !selections ||
    draft.mode !== "individual" ||
    publication.locationId !== draft.locationId ||
    assignment.taskBindings.some((task) => {
      const row = publication.rows.find(
        ({ offeringId }) => offeringId === task.offeringId,
      );

      return !row?.professionalUserLocationIds.includes(
        assignment.userLocationId,
      );
    })
  ) {
    return null;
  }

  const source = input.source ?? specialistServicesRouteCapabilityFixtureSource;
  let routeData: SpecialistServicesRouteData | null;

  try {
    routeData = source.resolveRouteData(
      draft.locationId,
      assignment.userLocationId,
    );
  } catch {
    return null;
  }

  if (
    !routeData ||
    !routeData.assignment.isPublicProfileVisible ||
    routeData.assignment.locationId !== draft.locationId ||
    routeData.assignment.userLocationId !== assignment.userLocationId ||
    routeData.workplace.locationId !== draft.locationId ||
    routeData.workplace.catalogVersion !== publication.catalogVersion
  ) {
    return null;
  }

  const rebuilt = createSpecialistServicesAppointmentDraft({
    draftId: draft.draftId,
    mode: draft.mode,
    assignment: routeData.assignment,
    workplace: routeData.workplace,
    services: routeData.services,
    offerings: routeData.offerings,
    selections,
    groupGuests: [],
  });

  if (!rebuilt.ok) {
    return null;
  }

  const rebuiltPlan = createSpecialistRouteTaskGraphLineage(rebuilt.draft);
  const currentPlan = createSpecialistRouteTaskGraphLineage(draft);

  if (
    !rebuiltPlan ||
    !currentPlan ||
    !haveExactSerializableStructure(rebuiltPlan, currentPlan)
  ) {
    return null;
  }

  const routeStaffIndependentPlan = rebuiltPlan;

  return Object.freeze({
    schemaVersion: SPECIALIST_SERVICES_ROUTE_CAPABILITY_SCHEMA_VERSION,
    kind: "specialist-services-route-capability" as const,
    appointmentDraftSchemaVersion: draft.schemaVersion,
    draftId: draft.draftId,
    locationId: draft.locationId,
    userLocationId: assignment.userLocationId,
    catalogVersion: publication.catalogVersion,
    eligibilityProviderId: publication.providerId,
    eligibilityProviderRevision: publication.providerRevision,
    taskBindings: Object.freeze(
      assignment.taskBindings.map((task) => Object.freeze({ ...task })),
    ),
    selections: Object.freeze(
      selections.map((selection) => Object.freeze(cloneSelection(selection))),
    ),
    routeServiceIds: Object.freeze(
      routeData.services.map(({ serviceId }) => serviceId),
    ),
    routeOfferingIds: Object.freeze(
      routeData.offerings.map(({ offeringId }) => offeringId),
    ),
    routeStaffIndependentPlan,
  });
}

/**
 * Exact-binds a freshly resolved route capability to the working draft and
 * the same current Location publication already authorizing Time mutation.
 */
export function specialistServicesRouteCapabilityMatchesDraft(input: {
  capability: SpecialistServicesRouteCapabilityV1 | null | undefined;
  draft: AppointmentBookingDraftV1;
  currentLocationEligibilityPublication:
    LocationAppointmentEligibilityPublicationV1 | null | undefined;
  userLocationId: string;
}) {
  const capability = input.capability;
  const publication = parseAppointmentLocationEligibilityPublication(
    input.currentLocationEligibilityPublication,
  );
  const validation = validateAppointmentBookingDraft(input.draft);

  if (
    !capability ||
    !publication ||
    !validation.valid ||
    capability.schemaVersion !==
      SPECIALIST_SERVICES_ROUTE_CAPABILITY_SCHEMA_VERSION ||
    capability.kind !== "specialist-services-route-capability"
  ) {
    return false;
  }

  const draft = validation.draft;
  const assignment = resolveCommonSpecificProfessional(draft);
  const selections = getOrganizerSelections(draft);
  const currentPlan = createSpecialistRouteTaskGraphLineage(draft);

  return Boolean(
    assignment &&
    selections &&
    assignment.userLocationId === input.userLocationId &&
    capability.appointmentDraftSchemaVersion === draft.schemaVersion &&
    capability.draftId === draft.draftId &&
    capability.locationId === draft.locationId &&
    capability.userLocationId === input.userLocationId &&
    capability.catalogVersion === publication.catalogVersion &&
    capability.eligibilityProviderId === publication.providerId &&
    capability.eligibilityProviderRevision === publication.providerRevision &&
    currentPlan &&
    haveExactSerializableStructure(
      capability.routeStaffIndependentPlan,
      currentPlan,
    ) &&
    haveExactSerializableStructure(
      capability.taskBindings,
      assignment.taskBindings,
    ) &&
    haveExactSerializableStructure(capability.selections, selections) &&
    selections.every((selection) =>
      capability.routeServiceIds.includes(selection.serviceId),
    ) &&
    capability.routeServiceIds.length === capability.routeOfferingIds.length &&
    new Set(capability.routeServiceIds).size ===
      capability.routeServiceIds.length &&
    new Set(capability.routeOfferingIds).size ===
      capability.routeOfferingIds.length,
  );
}
